import React, { useEffect, useRef, useState, useContext } from "react"
import tw from "twin.macro"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import CLink from "../cLink"
import { GeneralContext } from "../../contexts/generalContext"
import { mapStyleObj, mapStyles } from "./mapStyle"




const StoreView = ({ store_data }) => {
  const generalContext = useContext(GeneralContext)
  const { t } = generalContext
  const mapContainer = useRef(null)
  const map = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  // Extraer coordenadas GPS
  const getCoordinates = () => {
    if (!store_data?.coordenadasGps) return null
    
    const coords = store_data.coordenadasGps.split('|')
    if (coords.length === 2) {
      return {
        lat: parseFloat(coords[0]),
        lng: parseFloat(coords[1])
      }
    }
    return null
  }

  const coordinates = getCoordinates()

  // Inyectar estilos CSS para el mapa oscuro
  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.textContent = mapStyles
    document.head.appendChild(styleElement)
    
    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  // Inicializar el mapa
  useEffect(() => {
    if (!coordinates || map.current || !mapContainer.current) return

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: mapStyleObj,
      center: [coordinates.lng, coordinates.lat],
      zoom: 15,
      scrollZoom: false, // Desactivar zoom con rueda del ratón
      boxZoom: true, // Permitir zoom con selección de área
      doubleClickZoom: true, // Permitir zoom con doble clic
      touchZoomRotate: true // Permitir zoom táctil en móviles
    })

    // Añadir controles de zoom
    map.current.addControl(new maplibregl.NavigationControl({
      showCompass: false,
      showZoom: true
    }), 'top-right')

    map.current.on('load', () => {
      setMapLoaded(true)
      // El estilo personalizado ya está aplicado en la configuración inicial
    })

    // Cleanup
    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [coordinates])

  // Añadir marcador cuando el mapa esté cargado
  useEffect(() => {
    if (!mapLoaded || !map.current || !coordinates) return

    // Crear elemento del marcador personalizado
    const markerElement = document.createElement('div')
    markerElement.innerHTML = `
      <div style="
        width: 40px;
        height: 40px;
        background-color: #847360;
        border: 3px solid #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 16px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.5);
        cursor: pointer;
        transition: transform 0.2s ease;
      ">
        OB
      </div>
    `

    // Añadir efecto hover al marcador
    markerElement.addEventListener('mouseenter', () => {
      markerElement.firstElementChild.style.transform = 'scale(1.1)'
    })
    markerElement.addEventListener('mouseleave', () => {
      markerElement.firstElementChild.style.transform = 'scale(1)'
    })

    // Crear marcador
    new maplibregl.Marker(markerElement)
      .setLngLat([coordinates.lng, coordinates.lat])
      .addTo(map.current)

    // Crear popup con información de la tienda (estilo oscuro)
    const popup = new maplibregl.Popup({
      offset: 25,
      closeButton: true,
      closeOnClick: false,
      className: 'dark-popup'
    }).setHTML(`
      <div style="
        padding: 16px; 
        min-width: 280px;
        background-color: #2a2a2a;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.6);
      ">
        <h3 style="
          margin: 0 0 12px 0; 
          font-weight: bold; 
          font-size: 18px; 
          color: #ffffff;
          line-height: 1.3;
        ">
          ${store_data.title}
        </h3>
        <p style="
          margin: 0 0 8px 0; 
          font-size: 14px; 
          color: #cccccc;
          line-height: 1.4;
        ">
          ${store_data.direccion}
        </p>
        <p style="
          margin: 0 0 8px 0; 
          font-size: 13px; 
          color: #999999;
        ">
          ${store_data.codigoPostal} ${store_data.localidad.title}
        </p>
        ${store_data.telefonoMovil ? `
          <p style="
            margin: 0; 
            font-size: 14px; 
            color: #847360; 
            font-weight: bold;
          ">
            📞 ${store_data.telefonoMovil}
          </p>
        ` : ''}
      </div>
    `)

    // Mostrar popup automáticamente
    popup.addTo(map.current)
    markerElement.addEventListener('click', () => {
      popup.addTo(map.current)
    })

  }, [mapLoaded, coordinates, store_data])

  // Función para abrir Google Maps
  const openInGoogleMaps = () => {
    if (!coordinates) return
    
    const url = `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`
    window.open(url, '_blank')
  }

  // Función para formatear la descripción HTML
  const formatDescription = (description) => {
    if (!description) return ""
    
    // Formatear HTML con estilos inline para compensar la falta de prose
    return description
      .replace(/<h3>/g, '<h3 style="color: #847360; font-weight: bold; margin: 1.5rem 0 1rem 0; font-size: 1.25rem; line-height: 1.4;">')
      .replace(/<\/h3>/g, '</h3>')
      .replace(/<p>/g, '<p style="margin: 1rem 0; line-height: 1.6;">')
      .replace(/<\/p>/g, '</p>')
      .replace(/\n/g, '<br style="margin: 0.5rem 0;">') // Convertir saltos de línea
      .replace(/\[...\]/g, '') // Remover [...] 
      .replace(/<br style="margin: 0\.5rem 0;"><br style="margin: 0\.5rem 0;">/g, '<br style="margin: 1rem 0;">') // Evitar dobles br
  }

  return (
    <>
    <div css={tw`w-full`}>
      {/* Columnas */}
      <div
        tw="md:grid grid-cols-3 gap-0"
        style={{
          height: '260px'
        }}
      >
        {/* COL IZQ: Informacion de la tienda */}
        <div css={tw`px-4 py-5 h-full overflow-y-auto bg-white text-obmed4 text-gris1`}>

          <p css={tw``}>
            {store_data.direccion}
            <br />
            {store_data.codigoPostal} {store_data.localidad.title}
            {store_data.provincia.title && store_data.provincia.title !== store_data.localidad.title && (
              <span css={tw`text-marron3`}> ({store_data.provincia.title})</span>
            )}
          </p>
          <div><a href={`tel:${store_data.telefonoMovil}`}><strong>{store_data.telefonoMovil}</strong></a></div>
          <div><a href={`mailto:${store_data.email}`}>{store_data.email}</a></div>
          <div><a href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`} target="_blank" rel="noopener noreferrer">{t?.storeView?.verEnMapa || "Ver en Google Maps"}</a></div>

        </div>

        {/* COL DER: Mapa */}
        <div css={tw`col-span-2`}>
            <div 
              ref={mapContainer} 
              css={tw`relative w-full h-full`}
            />
            
            {/* Loading overlay */}
            {!mapLoaded && (
              <div css={tw`absolute inset-0 bg-gray-100 flex items-center justify-center`}>
                <div css={tw`text-center`}>
                  <div css={tw`animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2`}></div>
                  <p css={tw`text-sm text-gray-600`}>Loading...</p>
                </div>
              </div>
            )}
          </div>

      </div>

      


    </div>


      {/* {store_data.description && (
        <div css={tw`mb-8`}>
          <h2 css={tw`text-xl font-semibold text-gris1 mb-4`}>
            {t?.storeView?.sobreNosotros || "Sobre nosotros"}
          </h2>
          <div 
            css={tw`text-gris1 leading-relaxed`}
            dangerouslySetInnerHTML={{ 
              __html: formatDescription(store_data.description) 
            }}
          />
        </div>
      )}

      <div css={tw`bg-gray-50 p-6 rounded-lg`}>
        <h3 css={tw`text-lg font-semibold text-gris1 mb-3`}>
          {t?.storeView?.informacionAdicional || "Información adicional"}
        </h3>
        <div css={tw`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gris1`}>
          {store_data.pwid && (
            <div>
              <span css={tw`font-semibold`}>ID Tienda:</span> {store_data.idstore}
            </div>
          )}
          {store_data.codigoPostal && (
            <div>
              <span css={tw`font-semibold`}>Código Postal:</span> {store_data.codigoPostal}
            </div>
          )}
          {store_data.pais_slug && (
            <div>
              <span css={tw`font-semibold`}>País:</span> {store_data.pais_slug.toUpperCase()}
            </div>
          )}
        </div>
      </div> */}
    </>
  )
}

export default StoreView